import { useState } from 'react';
import { UserPlus, UserMinus } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';

function formatExpiry(value) {
    if (!value) return '';
    const normalized = String(value).replace(' ', 'T');
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export default function SessionWaitlistSection({ session, users = [], onChanged }) {
    const { getAuthenticatedApi } = useAuth();
    const [showAdd, setShowAdd] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [busy, setBusy] = useState(false);

    if (!session) return null;

    const waitlist = session.waitlist || [];
    const max = Number(session.max_participants || 0);
    const hasCap = max > 0;
    const enrolledCount = Number(session.current_participants || 0);
    // Posti davvero liberi (non riservati da offerte): usati per "Iscrivi subito"
    const physicalFreeSpots = hasCap ? Math.max(0, max - enrolledCount) : 1;
    // Posti disponibili per nuove iscrizioni senza toccare offerte aperte
    const freeSpots = hasCap
        ? max - enrolledCount - Number(session.active_offers || 0)
        : 1;
    const canAdd = hasCap && freeSpots <= 0;
    if (!hasCap && waitlist.length === 0) return null;

    const enrolledIds = new Set((session.enrollments || []).map((e) => Number(e.user_id)));
    const waitingIds = new Set(waitlist.map((row) => Number(row.user_id)));
    const candidates = users.filter(
        (user) => !enrolledIds.has(Number(user.id)) && !waitingIds.has(Number(user.id))
    );

    const refresh = () => {
        setShowAdd(false);
        setSelectedUsers([]);
        if (onChanged) onChanged();
    };

    const addUsers = async () => {
        if (!selectedUsers.length) return;
        setBusy(true);
        try {
            const api = getAuthenticatedApi();
            const response = await api.post('/gym_courses.php?action=add_to_waitlist', {
                session_id: session.id,
                user_ids: selectedUsers,
            });
            const added = response.data.added_count || 0;
            const errors = response.data.errors || [];
            if (added > 0) {
                toast.success(`${added} utenti aggiunti alla lista di attesa`);
            }
            if (errors.length > 0) {
                toast.error(errors[0]);
            }
            if (added === 0 && errors.length === 0) {
                toast.error('Nessun inserimento in lista');
            }
            refresh();
        } catch (err) {
            toast.error(err.response?.data?.error || err.response?.data?.message || 'Errore lista di attesa');
        } finally {
            setBusy(false);
        }
    };

    const removeUser = async (row) => {
        if (!window.confirm(`Togliere ${row.user_name} dalla lista di attesa?`)) return;
        try {
            const api = getAuthenticatedApi();
            await api.post('/gym_courses.php?action=remove_from_waitlist', {
                waitlist_id: row.id,
            });
            toast.success('Utente rimosso dalla lista');
            refresh();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Errore durante la rimozione');
        }
    };

    const enrollNow = async (row) => {
        if (physicalFreeSpots <= 0) return;
        const message =
            row.status === 'offered'
                ? `Confermare l'iscrizione di ${row.user_name}? (ha già un'offerta aperta)`
                : freeSpots <= 0
                  ? `Iscrivere ${row.user_name} subito? Verrà usato un posto attualmente riservato da un'offerta.`
                  : `Iscrivere ${row.user_name} subito?`;
        if (!window.confirm(message)) return;
        try {
            const api = getAuthenticatedApi();
            await api.post('/gym_courses.php?action=enroll_users', {
                session_id: session.id,
                user_ids: [row.user_id],
                send_notification: true,
            });
            toast.success('Utente iscritto');
            refresh();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Errore durante l\'iscrizione');
        }
    };

    return (
        <div className="mt-8">
            <div className="mb-4 flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Lista di attesa ({waitlist.length})
                </h4>
                {canAdd && (
                    <button
                        type="button"
                        onClick={() => setShowAdd(true)}
                        className="flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm text-white hover:bg-amber-700"
                    >
                        <UserPlus size={16} />
                        Aggiungi alla lista
                    </button>
                )}
            </div>
            {Number(session.active_offers || 0) > 0 && (
                <p className="mb-3 text-sm text-amber-700 dark:text-amber-300">
                    {session.active_offers} {Number(session.active_offers) === 1 ? 'posto riservato' : 'posti riservati'} in attesa di conferma
                </p>
            )}
            {waitlist.length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-500 dark:text-white/65">
                    Nessuno in lista di attesa
                </p>
            ) : (
                <div className="max-h-80 space-y-2 overflow-y-auto">
                    {waitlist.map((row) => (
                        <div
                            key={row.id}
                            className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-white/10 dark:bg-gray-900"
                        >
                            <div className="min-w-0">
                                <p className="font-medium text-gray-900 dark:text-white">
                                    {row.position}. {row.user_name}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-white/65">{row.user_email}</p>
                                <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
                                    {row.status === 'offered'
                                        ? `Offerta aperta fino al ${formatExpiry(row.offer_expires_at)}`
                                        : 'In attesa'}
                                </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                                {physicalFreeSpots > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => enrollNow(row)}
                                        className="rounded-lg px-2 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
                                    >
                                        Iscrivi subito
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => removeUser(row)}
                                    className="rounded-lg p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                    title="Togli dalla lista"
                                >
                                    <UserMinus size={18} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showAdd && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 dark:bg-gray-900">
                        <h4 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                            Aggiungi alla lista di attesa
                        </h4>
                        <div className="mb-4 max-h-80 space-y-2 overflow-y-auto">
                            {candidates.length === 0 ? (
                                <p className="text-sm text-gray-500 dark:text-white/65">Nessun utente disponibile</p>
                            ) : candidates.map((user) => (
                                <label
                                    key={user.id}
                                    className="flex cursor-pointer items-center gap-3 rounded p-2 hover:bg-gray-50 dark:hover:bg-white/[0.06]"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedUsers.includes(user.id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedUsers([...selectedUsers, user.id]);
                                            } else {
                                                setSelectedUsers(selectedUsers.filter((id) => id !== user.id));
                                            }
                                        }}
                                        className="h-4 w-4 rounded text-amber-600"
                                    />
                                    <span className="text-gray-900 dark:text-white">{user.name}</span>
                                </label>
                            ))}
                        </div>
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowAdd(false);
                                    setSelectedUsers([]);
                                }}
                                className="rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-white/85 dark:hover:bg-white/10"
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                onClick={addUsers}
                                disabled={busy || selectedUsers.length === 0}
                                className="rounded-lg bg-amber-600 px-4 py-2 text-white hover:bg-amber-700 disabled:opacity-50"
                            >
                                Aggiungi ({selectedUsers.length})
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
